import type { RouteRecordRaw } from 'vue-router';
import SmeCampaignReport from '@/views/sme/pages/report/SmeCampaignReport.vue';
import { useSmeAuthStore } from '@/store/sme/sme-auth';

const SmeAuth = () => import('@/views/sme/pages/auth/SmeAuth.vue');
const SmeApp = () => import('@/views/sme/SmeApp.vue');
const SmeDashboard = () => import('@/views/sme/pages/dashboard/SmeDashboard.vue');
const SmeCreateCampaign = () => import('@/views/sme/pages/create-campaign/SmeCreateCampaign.vue');
const SmeCampaign = () => import('@/views/sme/pages/campaign/SmeCampaign.vue');
const SmeReport = () => import('@/views/sme/pages/report/SmeReport.vue');
const SmeSettings = () => import('@/views/sme/pages/settings/SmeSettings.vue');
const SmeContent = () => import('@/views/sme/pages/content/SmeContent.vue');
const SmeProfileEdit = () => import('@/views/sme/pages/settings/SmeProfileEdit.vue');
const SmeBusinessEdit = () => import('@/views/sme/pages/settings/SmeBusinessEdit.vue');
const CampaignReports = () => import('@/views/public/sme/CampaignReports.vue');

export const smeRoutes: RouteRecordRaw[] = [
  {
    path: '/sme/',
    name: 'sme',
    component: SmeAuth,
    meta: { requireSmeAnonymous: true },
  },
  {
    path: '/sme/public/reports/:campaign_id',
    name: 'sme-public-report',
    component: CampaignReports,
    meta: { requireSmeAnonymous: true },
  },
  {
    path: '/sme/auth/',
    name: 'sme-auth',
    component: SmeAuth,
    meta: { requireSmeAnonymous: true },
    children: [
      {
        path: '',
        name: 'sme-login-redirect',
        redirect: { name: 'sme-signin' },
        meta: { requireAnonymous: true },
      },
      {
        path: 'signin',
        name: 'sme-signin',
        component: SmeAuth,
        meta: { requireSmeAnonymous: true },
      },
      {
        path: 'forgot-password',
        name: 'sme-forgot-password',
        component: SmeAuth,
        meta: { requireSmeAnonymous: true },
      },
      {
        path: 'reset-password',
        name: 'sme-reset-password',
        component: SmeAuth,
        meta: { requireSmeAnonymous: true },
      },
      {
        path: 'signup',
        name: 'sme-signup',
        component: SmeAuth,
        meta: { requireSmeAnonymous: true },
      },
      {
        path: 'complete-signup',
        name: 'sme-complete-signup',
        component: SmeAuth,
      },
    ],
  },
  {
    path: '/sme/auth/create-brand',
    name: 'sme-create-brand',
    component: SmeAuth,
    meta: { requireSmeAuthenticated: true },
  },
  {
    path: '/sme/:sme_id',
    name: 'SME',
    component: SmeApp,
    // redirect: { name: 'sme-dashboard' },
    meta: { requireSmeAuthenticated: true },
    beforeEnter: async (to, from, next) => {
      const smeAuthStore = useSmeAuthStore();

      if (!smeAuthStore.smeProfile) {
        await smeAuthStore.doGetSmeProfile();
      }

      const requestedSmeId = to.params.sme_id as string;
      const isValidCompany = smeAuthStore.smeProfile.companies.some((company: any) => company.id === requestedSmeId);

      if (isValidCompany) {
        next(); // Allow navigation
      } else {
        // Redirect to user's primary company
        const primaryCompany = smeAuthStore.smeProfile.companies.find((company: any) => company.company_role === 'COMPANY');

        if (primaryCompany) {
          next({
            name: 'sme-dashboard',
            params: { sme_id: primaryCompany.id },
          });
        } else {
          next(false);
        }
      }
    },
    children: [
      {
        path: 'dashboard',
        name: 'sme-dashboard',
        component: SmeDashboard,
      },
      {
        path: 'campaigns/create',
        name: 'sme-create-campaign',
        component: SmeCreateCampaign,
      },
      {
        path: 'campaigns',
        name: 'sme-campaign',
        component: SmeCampaign,
      },
      {
        path: 'reports',
        name: 'sme-reports',
        component: SmeReport,
      },
      {
        path: 'reports/:campaign_id',
        name: 'sme-campaign-report',
        component: SmeCampaignReport,
      },
      {
        path: 'contents',
        name: 'sme-contents',
        component: SmeContent,
      },
      {
        path: 'settings',
        name: 'sme-settings',
        component: SmeSettings,
      },
      {
        path: 'settings/edit-profile',
        name: 'sme-profile',
        component: SmeProfileEdit,
      },
      {
        path: 'settings/edit-business',
        name: 'sme-business',
        component: SmeBusinessEdit,
      },
    ],
  },
];
